import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  resetConsentState,
  setConsentLoader,
  setConsentsDetails,
  setConsentSubmitting,
} from "../reducers/providerConsentSlice";
import { AppThunk } from "../store";

export const loadConsentDetails =
  (
    studyId: string,
    participantId: any,
    formId: any,
    versionId: string
  ): AppThunk =>
  async (dispatch) => {
    try {
      if (!studyId || !formId) {
        dispatch(resetConsentState());
        return;
      }

      dispatch(setConsentLoader(true));

      const res: AxiosResponse = await http.get(
        `/admin/study/${studyId}/econsent/participants/${participantId}/${formId}/versions/${versionId}`
      );
      const data = res.data?.data;
      const formStatus = data?.status;
      const consentName = data?.consentVersion?.name;

      if (data) {
        let blocks = data?.consentVersion?.blocks
          ?.slice()
          .sort(
            (a: { position: number }, b: { position: number }) =>
              a.position - b.position
          );

        // let providerResponse = blocks.reduce((acc: any, block: any) => {
        //   if (block.type === "signature" && block.config?.provider) {
        //     acc[block.id] = {
        //       position: block.position,
        //       ...(block.config?.name && {
        //         firstName: "",
        //         lastName: "",
        //         name: "",
        //       }),
        //       ...(block.config?.address && { address: "" }),
        //       ...(block.config?.date && { date: null }),
        //       signature: "",
        //     };
        //   }
        //   return acc;
        // }, {});

        let blocksWithResponses = blocks.map((block: any) => ({
          ...block,
          editable:
            block.type === "signature" &&
            block.config?.provider &&
            !data?.response?.[block.id]?.signature,
          response: data?.response?.[block.id] || null,
        }));

        // console.log({ providerResponse });

        dispatch(
          setConsentsDetails({
            consentName: consentName,
            error: "",
            loading: false,
            formSubmitting: false,
            formDirty: false,
            status: formStatus,
            blocks: blocksWithResponses,
            formId: formId,
            studyId: studyId,
            readonly: false,
            // providerResponse,
          })
        );
      }
    } catch (err) {
      dispatch(setConsentSubmitting(false));
      dispatch(setConsentLoader(false));
      errorToastMessage(err as Error);
    }
  };

export const loadParticipantConsentDetails =
  (studyId: string, formId: any, versionId: string): AppThunk =>
  async (dispatch) => {
    try {
      if (!studyId || !formId) {
        dispatch(resetConsentState());
        return;
      }

      dispatch(setConsentLoader(true));

      const res: AxiosResponse = await http.get(
        `/participant/study/${studyId}/econsent/${formId}/versions/${versionId}`
      );

      const data = res.data?.data;
      const formStatus = data?.status;
      const editable =
        formStatus === "not_started" || formStatus === "in_progress";
      const consentName = data?.consentVersion?.name;

      if (data) {
        let blocks = data?.consentVersion?.blocks
          ?.slice()
          .sort(
            (a: { position: number }, b: { position: number }) =>
              a.position - b.position
          );

        let blocksWithResponses = blocks.map((block: any) => ({
          ...block,
          editable:
            !(
              block.config?.provider || data?.response?.[block.id]?.signature
            ) && editable,
          response: data?.response?.[block.id] || {},
        }));

        dispatch(
          setConsentsDetails({
            consentName: consentName,
            error: "",
            loading: false,
            formSubmitting: false,
            formDirty: false,
            status: formStatus,
            blocks: blocksWithResponses,
            formId: formId,
            studyId: studyId,
            readonly: !editable,
          })
        );
      }
    } catch (err) {
      dispatch(setConsentSubmitting(false));
      dispatch(setConsentLoader(false));
      errorToastMessage(err as Error);
    }
  };

export const finalizeConsent =
  (
    studyId: string,
    formId: any,
    versionId: string
  ): AppThunk<Promise<boolean>> =>
  async (dispatch, getState): Promise<boolean> => {
    try {
      dispatch(setConsentSubmitting(true));
      const { blocks } = getState().providerConsent;
      const body: any = {
        blocks: [],
      };
      for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i];
        if (block.editable) {
          switch (block.type) {
            case "signature": {
              if (block.editable) {
                toastMessage(
                  "warning",
                  "Please sign the signature fields before finalizing it."
                );
                dispatch(setConsentSubmitting(false));
                return false;
              } else {
                if (!block.config.provider) {
                  body.blocks.push({
                    blockId: block.id,
                    response: block.response,
                  });
                }
              }
              break;
            }
            case "checkbox": {
              if (!block.response?.checked && !block.config?.optional) {
                toastMessage(
                  "warning",
                  "All checkboxes needs to be checked before finalizing the consent form."
                );
                dispatch(setConsentSubmitting(false));
                return false;
              }
              if (block.response.checked) {
                body.blocks.push({
                  blockId: block.id,
                  response: block.response,
                });
              }
              break;
            }
            case "radio": {
              if (!block.response?.radioKey) {
                toastMessage(
                  "warning",
                  "All Radio fields needs to be checked before finalizing the consent form."
                );
                dispatch(setConsentSubmitting(false));
                return false;
              }
              if (block.response.radioKey) {
                body.blocks.push({
                  blockId: block.id,
                  response: block.response,
                });
              }
              break;
            }
            case "input": {
              if (!block.response?.input && !block.config?.optional) {
                toastMessage(
                  "warning",
                  "All input fields needs to be answered before finalizing the consent form."
                );
                dispatch(setConsentSubmitting(false));
                return false;
              }
              if (block.response.input) {
                body.blocks.push({
                  blockId: block.id,
                  response: block.response,
                });
              }
              break;
            }
          }
        } else {
          if (block.type === "signature" && !block.config.provider) {
            body.blocks.push({
              blockId: block.id,
              response: block.response,
            });
          }
        }
      }
      await http.post(
        `/participant/study/${studyId}/econsent/${formId}/versions/${versionId}/save`,
        body
      );
      const res1: AxiosResponse = await http.post(
        `/participant/study/${studyId}/econsent/${formId}/versions/${versionId}/finalize`,
        body
      );
      toastMessage("success", res1.data.message);
      dispatch(setConsentLoader(false));
      return true;
    } catch (err) {
      dispatch(setConsentSubmitting(false));
      dispatch(setConsentLoader(false));
      errorToastMessage(err as Error);
      return false;
    }
  };
